import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { Grid, Typography, Box, Button, IconButton, Tooltip } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { BackstageTheme } from '@backstage/theme';
import { InfoCard, Link, Page, Content } from '@backstage/core-components';
import TuneIcon from '@material-ui/icons/Tune';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VisibilityIcon from '@material-ui/icons/Visibility';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import BaseSkyBannerLight from '../../assets/Base_sky_1.png';
import BaseSkyBannerDark from '../../assets/Base_sky_6.png';
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useHomepageTiles } from './useHomepageTiles';
import { SystemTile, DEFAULT_TILES, PREFERENCES_VERSION } from './tiles';
import { useHomeAnalytics } from './useHomeAnalytics';

const useStyles = makeStyles<BackstageTheme>(theme => ({
  pageWrapper: {
    width: '100%',
    minHeight: '100vh',
    position: 'relative',
    backgroundColor: theme.palette.background.default,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top center',
    backgroundAttachment: 'scroll',
  },
  container: {
    position: 'relative',
    zIndex: 1,
    padding: theme.spacing(4),
    maxWidth: '100%',
    margin: '0 auto',
    paddingTop: theme.spacing(20),
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(8),
    position: 'relative',
  },
  title: {
    color: '#F9F5F4',
    fontWeight: 700,
    textAlign: 'center',
  },
  buttonGroup: {
    position: 'absolute',
    right: 0,
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  actionButton: {
    color: '#F9F5F4',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    '&:focus-visible': {
      outline: '2px solid #F9F5F4',
      outlineOffset: 2,
    },
  },
  destructiveButton: {
    color: '#F9F5F4',
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  bannerText: {
    color: '#F9F5F4',
  },
  quickLinkCard: {
    height: '100%',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
    },
  },
  cardLink: {
    display: 'block',
    height: '100%',
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    textAlign: 'center',
    minHeight: '200px',
  },
  cardIcon: {
    fontSize: '64px',
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  cardTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color:
      theme.palette.type === 'dark'
        ? '#F9F5F4'
        : '#5F016F',
  },
  cardDescription: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8, 4),
    color: '#F9F5F4',
  },
  emptyStateButton: {
    color: '#F9F5F4',
    borderColor: '#F9F5F4',
  },
  newBadge: {
    marginLeft: theme.spacing(1),
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    verticalAlign: 'middle',
  },
  skeletonCard: {
    borderRadius: 0,
  },
  editModeCard: {
    height: '100%',
    position: 'relative',
    border: `2px dashed ${theme.palette.primary.main}`,
  },
  editModeCardDragging: {
    opacity: 0.3,
  },
  hiddenCard: {
    height: '100%',
    position: 'relative',
    opacity: 0.4,
    filter: 'grayscale(80%)',
    border: `2px dashed ${theme.palette.text.disabled}`,
  },
  tileOverlay: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 2,
  },
  overlayButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: '#fff',
    padding: theme.spacing(0.75, 2),
    borderRadius: theme.spacing(2),
    fontSize: '0.75rem',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
    },
  },
  dragHandle: {
    position: 'absolute',
    top: theme.spacing(1),
    left: theme.spacing(1),
    zIndex: 2,
    cursor: 'grab',
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5),
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  dragOverlayCard: {
    opacity: 0.9,
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 0,
    backgroundColor: theme.palette.background.paper,
  },
  hiddenSectionHeading: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1.5),
  },
  confirmReset: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  },
}));

function SortableTileCard({
  tile,
  children,
  className,
  activeId,
}: {
  tile: SystemTile;
  children: ReactNode;
  className: string;
  activeId: string | null;
}) {
  const classes = useStyles();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Grid item xs={12} sm={6} md={4}>
      <div
        ref={setNodeRef}
        style={{ ...style, height: '100%', position: 'relative', cursor: 'grab' }}
        {...attributes}
        {...listeners}
        aria-describedby="dnd-instructions"
      >
        <Box className={classes.dragHandle} aria-hidden="true">
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <InfoCard
          className={`${className} ${isDragging && activeId === tile.id ? classes.editModeCardDragging : ''}`}
          noPadding
        >
          {children}
        </InfoCard>
      </div>
    </Grid>
  );
}

export const ArriveHome = () => {
  const classes = useStyles();
  const theme = useTheme<BackstageTheme>();
  const { trackTileClick } = useHomeAnalytics();
  const [editMode, setEditMode] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);

  const [localVisible, setLocalVisible] = useState<SystemTile[]>([]);
  const [localHidden, setLocalHidden] = useState<SystemTile[]>([]);
  const [savePending, setSavePending] = useState(false);

  const noButtonRef = useRef<HTMLButtonElement>(null);

  const {
    visibleTiles,
    hiddenTiles,
    newTileIds,
    loading,
    preferences,
    savePreferences,
    resetPreferences,
  } = useHomepageTiles();

  // Clear savePending when hook data updates after save
  const prevVisibleRef = useRef(visibleTiles);
  useEffect(() => {
    if (savePending && visibleTiles !== prevVisibleRef.current) {
      setSavePending(false);
    }
    prevVisibleRef.current = visibleTiles;
  }, [visibleTiles, savePending]);

  // Announce edit mode changes to screen readers
  const prevEditModeRef = useRef(false);
  useEffect(() => {
    if (editMode && !prevEditModeRef.current) {
      setAnnouncement('Customization mode enabled');
    } else if (!editMode && prevEditModeRef.current) {
      setAnnouncement('Customization mode disabled');
    }
    prevEditModeRef.current = editMode;
  }, [editMode]);

  // Focus management for reset confirmation
  useEffect(() => {
    if (confirmingReset) {
      noButtonRef.current?.focus();
    }
  }, [confirmingReset]);

  // Escape key handler
  useEffect(() => {
    if (!editMode) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirmingReset) {
          setConfirmingReset(false);
        } else {
          setEditMode(false);
          setConfirmingReset(false);
          setAnnouncement('Customization mode disabled');
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editMode, confirmingReset]);

  const bannerSrc =
    theme.palette.type === 'dark' ? BaseSkyBannerDark : BaseSkyBannerLight;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const enterEditMode = useCallback(() => {
    setLocalVisible(visibleTiles);
    setLocalHidden(hiddenTiles);
    setConfirmingReset(false);
    setSavePending(false);
    setEditMode(true);
  }, [visibleTiles, hiddenTiles]);

  const exitEditMode = useCallback(async () => {
    // Dismiss new tile badges on save
    const allDismissed = [
      ...new Set([
        ...localHidden.map(t => t.id),
        ...(preferences?.dismissedNewTileIds ?? []),
        ...newTileIds,
      ]),
    ];

    setSavePending(true);
    setEditMode(false);
    setConfirmingReset(false);

    const ok = await savePreferences({
      visibleTileIds: localVisible.map(t => t.id),
      dismissedNewTileIds: allDismissed,
      version: PREFERENCES_VERSION,
    });
    if (!ok) {
      setSavePending(false);
    }
  }, [localVisible, localHidden, preferences, newTileIds, savePreferences]);

  const cancelEditMode = useCallback(() => {
    setEditMode(false);
    setConfirmingReset(false);
  }, []);

  const handleHide = useCallback((tile: SystemTile) => {
    setLocalVisible(prev => prev.filter(t => t.id !== tile.id));
    setLocalHidden(prev => [...prev, tile]);
    setAnnouncement(`${tile.title} hidden`);
  }, []);

  const handleShow = useCallback((tile: SystemTile) => {
    setLocalHidden(prev => prev.filter(t => t.id !== tile.id));
    setLocalVisible(prev => [...prev, tile]);
    setAnnouncement(`${tile.title} shown`);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setLocalVisible(prev => {
      const oldIndex = prev.findIndex(t => t.id === active.id);
      const newIndex = prev.findIndex(t => t.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const handleReset = useCallback(async () => {
    if (!confirmingReset) {
      setConfirmingReset(true);
      return;
    }
    // Show default tiles during pending reset (not stale local state)
    setLocalVisible(DEFAULT_TILES);
    setLocalHidden([]);
    setSavePending(true);
    setEditMode(false);
    setConfirmingReset(false);
    setAnnouncement('Customization mode disabled');

    const ok = await resetPreferences();
    if (!ok) {
      setSavePending(false);
    }
  }, [confirmingReset, resetPreferences]);

  // Which tiles to render
  const displayVisible = editMode || savePending ? localVisible : visibleTiles;
  const displayHidden = editMode ? localHidden : [];

  // Find active tile for DragOverlay
  const activeTile = activeId
    ? displayVisible.find(t => t.id === activeId)
    : undefined;

  const renderCardBody = (tile: SystemTile, isHidden: boolean) => {
    const Icon = tile.icon;
    const isNew = !isHidden && newTileIds.includes(tile.id);
    return (
      <Box className={classes.cardContent}>
        <Icon className={classes.cardIcon} />
        <Typography variant="h5" className={classes.cardTitle}>
          {tile.title}
          {isNew && <span className={classes.newBadge}>New</span>}
        </Typography>
        <Typography variant="body2" className={classes.cardDescription}>
          {tile.description}
        </Typography>
      </Box>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Grid container spacing={3}>
          {[0, 1, 2, 3, 4, 5].map(i => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton
                variant="rect"
                height={200}
                className={classes.skeletonCard}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (!editMode && displayVisible.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="h5" gutterBottom>
            You have hidden all tiles
          </Typography>
          <Typography variant="body1" gutterBottom>
            Click Customize to show tiles on your homepage.
          </Typography>
          <Button
            variant="outlined"
            className={classes.emptyStateButton}
            onClick={enterEditMode}
          >
            Customize
          </Button>
        </Box>
      );
    }

    if (editMode) {
      return (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext
              items={displayVisible.map(t => t.id)}
              strategy={rectSortingStrategy}
            >
              <Grid container spacing={3}>
                {displayVisible.map(tile => (
                  <SortableTileCard
                    key={tile.id}
                    tile={tile}
                    className={classes.editModeCard}
                    activeId={activeId}
                  >
                    <Box className={classes.tileOverlay}>
                      <Button
                        className={classes.overlayButton}
                        onPointerDown={e => e.stopPropagation()}
                        onClick={() => handleHide(tile)}
                        startIcon={<VisibilityOffIcon />}
                        aria-label={`Hide ${tile.title}`}
                      >
                        Hide
                      </Button>
                    </Box>
                    {renderCardBody(tile, false)}
                  </SortableTileCard>
                ))}
              </Grid>
            </SortableContext>

            <DragOverlay>
              {activeTile ? (
                <div className={classes.dragOverlayCard}>
                  {renderCardBody(activeTile, false)}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          {displayHidden.length > 0 && (
            <section aria-label="Hidden tiles">
              <Typography
                variant="subtitle2"
                component="h3"
                className={classes.hiddenSectionHeading}
              >
                Hidden tiles
              </Typography>
              <Grid container spacing={3}>
                {displayHidden.map(tile => (
                  <Grid item xs={12} sm={6} md={4} key={tile.id}>
                    <InfoCard className={classes.hiddenCard} noPadding>
                      <Box className={classes.tileOverlay}>
                        <Button
                          className={classes.overlayButton}
                          onClick={() => handleShow(tile)}
                          startIcon={<VisibilityIcon />}
                          aria-label={`Show ${tile.title}`}
                        >
                          Show
                        </Button>
                      </Box>
                      {renderCardBody(tile, true)}
                    </InfoCard>
                  </Grid>
                ))}
              </Grid>
            </section>
          )}
        </>
      );
    }

    // Normal mode
    return (
      <Grid container spacing={3}>
        {displayVisible.map((tile, index) => (
          <Grid item xs={12} sm={6} md={4} key={tile.id}>
            <Link
              to={tile.url}
              className={classes.cardLink}
              onClick={() => trackTileClick(tile.title, index + 1)}
            >
              <InfoCard className={classes.quickLinkCard} noPadding>
                {renderCardBody(tile, false)}
              </InfoCard>
            </Link>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Page themeId="home">
      <Content noPadding>
        <Box
          className={classes.pageWrapper}
          style={{ backgroundImage: `url(${bannerSrc})` }}
        >
          <Box className={classes.container}>
            <Box className={classes.titleRow}>
              <Typography variant="h2" className={classes.title}>
                Welcome to Arrive Developer Portal
              </Typography>
              <Box className={classes.buttonGroup}>
                {editMode ? (
                  <>
                    {confirmingReset ? (
                      <Box className={classes.confirmReset}>
                        <Typography
                          variant="body2"
                          className={classes.bannerText}
                        >
                          Reset all?
                        </Typography>
                        <Button
                          ref={noButtonRef}
                          size="small"
                          className={classes.actionButton}
                          onClick={() => setConfirmingReset(false)}
                        >
                          No
                        </Button>
                        <Button
                          size="small"
                          className={classes.destructiveButton}
                          onClick={handleReset}
                        >
                          Yes
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        className={classes.actionButton}
                        onClick={handleReset}
                      >
                        Reset
                      </Button>
                    )}
                    <Button
                      size="small"
                      className={classes.actionButton}
                      onClick={cancelEditMode}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={exitEditMode}
                    >
                      Done
                    </Button>
                  </>
                ) : (
                  <Tooltip title="Customize tiles">
                    <IconButton
                      className={classes.actionButton}
                      onClick={enterEditMode}
                      aria-label="Customize homepage tiles"
                      color="default"
                      size="small"
                    >
                      <TuneIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {renderContent()}
          </Box>
        </Box>

        {/* Screen reader announcements */}
        <div
          aria-live="polite"
          aria-atomic="true"
          className={classes.srOnly}
        >
          {announcement}
        </div>

        {/* Keyboard drag-and-drop instructions */}
        <div id="dnd-instructions" className={classes.srOnly}>
          Press Space to pick up, arrow keys to move, Space to drop
        </div>
      </Content>
    </Page>
  );
};
